import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEmpFeatureFlag } from '@/hooks/useEmpFeatureFlag';
import { useAuth } from '@/contexts/AuthContext';

export interface ExploracaoFilters {
  clusterIds: string[];
  contratoId: string | null;
  municipioIbge: string | null;
  frenteId: string | null;
  periodoInicio: string | null;
  periodoFim: string | null;
}

export interface ExecucaoPorPeriodo {
  contrato_id: string;
  cluster_id: string;
  periodo: string;
  total_execucoes: number;
  volume_executado: number;
  frentes_ativas: number;
}

export interface ExecucaoPorMunicipio {
  contrato_id: string;
  cluster_id: string;
  municipio_ibge: string;
  municipio_nome: string;
  municipio_uf: string;
  total_execucoes: number;
  volume_executado: number;
}

export interface ExecucaoPorFrente {
  contrato_id: string;
  cluster_id: string;
  frente_id: string;
  frente_nome: string;
  total_execucoes: number;
  volume_executado: number;
  primeira_execucao: string;
  ultima_execucao: string;
}

export function useExploracaoOperacional(filters: ExploracaoFilters) {
  const { activeClusterIds } = useEmpFeatureFlag();
  const { role, gestorClusterIds } = useAuth();

  const scopeClusterIds = (() => {
    const base =
      role === 'gestor'
        ? activeClusterIds.filter(id => gestorClusterIds.includes(id))
        : activeClusterIds;
    if (filters.clusterIds.length > 0) {
      return base.filter(id => filters.clusterIds.includes(id));
    }
    return base;
  })();

  // Fetch contratos for filter dropdown
  const { data: contratos = [] } = useQuery({
    queryKey: ['exploracao-contratos', scopeClusterIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_contratos')
        .select('id, nome, cluster_id')
        .in('cluster_id', scopeClusterIds);
      if (error) throw error;
      return data || [];
    },
    enabled: scopeClusterIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch frentes for filter dropdown
  const contratoIdsForFrente = filters.contratoId ? [filters.contratoId] : contratos.map(c => c.id);
  const { data: frentes = [] } = useQuery({
    queryKey: ['exploracao-frentes', contratoIdsForFrente],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_frentes_trabalho')
        .select('id, nome, contrato_id, municipio_ibge')
        .in('contrato_id', contratoIdsForFrente)
        .eq('status', 'ativa');
      if (error) throw error;
      return data || [];
    },
    enabled: contratoIdsForFrente.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch clusters for filter dropdown
  const { data: clusters = [] } = useQuery({
    queryKey: ['exploracao-clusters', activeClusterIds],
    queryFn: async () => {
      const { data } = await supabase
        .from('clusters')
        .select('id, name')
        .in('id', activeClusterIds);
      return data || [];
    },
    enabled: activeClusterIds.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  // Query: execucoes por periodo
  const { data: porPeriodo = [], isLoading: loadingPeriodo } = useQuery({
    queryKey: ['exploracao-periodo', scopeClusterIds, filters],
    queryFn: async () => {
      let query = supabase
        .from('emp_vw_execucoes_por_periodo' as any)
        .select('*')
        .in('cluster_id', scopeClusterIds);
      if (filters.contratoId) query = query.eq('contrato_id', filters.contratoId);
      if (filters.periodoInicio) query = query.gte('periodo', filters.periodoInicio.slice(0, 7));
      if (filters.periodoFim) query = query.lte('periodo', filters.periodoFim.slice(0, 7));
      const { data, error } = await query as any;
      if (error) throw error;
      return (data || []) as ExecucaoPorPeriodo[];
    },
    enabled: scopeClusterIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  // Query: execucoes por municipio
  const { data: porMunicipio = [], isLoading: loadingMunicipio } = useQuery({
    queryKey: ['exploracao-municipio', scopeClusterIds, filters],
    queryFn: async () => {
      let query = supabase
        .from('emp_vw_execucoes_por_municipio' as any)
        .select('*')
        .in('cluster_id', scopeClusterIds);
      if (filters.contratoId) query = query.eq('contrato_id', filters.contratoId);
      if (filters.municipioIbge) query = query.eq('municipio_ibge', filters.municipioIbge);
      const { data, error } = await query as any;
      if (error) throw error;
      return (data || []) as ExecucaoPorMunicipio[];
    },
    enabled: scopeClusterIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  // Query: execucoes por frente
  const { data: porFrente = [], isLoading: loadingFrente } = useQuery({
    queryKey: ['exploracao-frente', scopeClusterIds, filters],
    queryFn: async () => {
      let query = supabase
        .from('emp_vw_execucoes_por_frente' as any)
        .select('*')
        .in('cluster_id', scopeClusterIds);
      if (filters.contratoId) query = query.eq('contrato_id', filters.contratoId);
      if (filters.frenteId) query = query.eq('frente_id', filters.frenteId);
      const { data, error } = await query as any;
      if (error) throw error;
      return (data || []) as ExecucaoPorFrente[];
    },
    enabled: scopeClusterIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const isLoading = loadingPeriodo || loadingMunicipio || loadingFrente;

  // Aggregate porPeriodo across contracts
  const evolucaoTemporal = (() => {
    const map = new Map<string, { total: number; volume: number }>();
    porPeriodo.forEach(r => {
      const existing = map.get(r.periodo) || { total: 0, volume: 0 };
      map.set(r.periodo, {
        total: existing.total + Number(r.total_execucoes),
        volume: existing.volume + Number(r.volume_executado),
      });
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([periodo, v]) => ({ periodo, ...v }));
  })();

  // Aggregate porMunicipio across contracts
  const distribuicaoMunicipio = (() => {
    const map = new Map<string, { nome: string; uf: string; total: number; volume: number }>();
    porMunicipio.forEach(r => {
      const key = r.municipio_ibge || 'unknown';
      const existing = map.get(key) || { nome: r.municipio_nome || 'Desconhecido', uf: r.municipio_uf || '', total: 0, volume: 0 };
      map.set(key, {
        ...existing,
        total: existing.total + Number(r.total_execucoes),
        volume: existing.volume + Number(r.volume_executado),
      });
    });
    return Array.from(map.values())
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 15);
  })();

  // Aggregate porFrente across contracts
  const rankingFrente = (() => {
    const map = new Map<string, { nome: string; total: number; volume: number }>();
    porFrente.forEach(r => {
      const key = r.frente_id || 'unknown';
      const existing = map.get(key) || { nome: r.frente_nome || 'Sem nome', total: 0, volume: 0 };
      map.set(key, {
        ...existing,
        total: existing.total + Number(r.total_execucoes),
        volume: existing.volume + Number(r.volume_executado),
      });
    });
    return Array.from(map.entries())
      .map(([id, v]) => ({ frente_id: id, ...v }))
      .sort((a, b) => b.volume - a.volume);
  })();

  // Unique municipios for filter
  const municipiosDisponiveis = (() => {
    const seen = new Map<string, string>();
    porMunicipio.forEach(r => {
      if (r.municipio_ibge && !seen.has(r.municipio_ibge)) {
        seen.set(r.municipio_ibge, `${r.municipio_nome || 'Desconhecido'}${r.municipio_uf ? ` - ${r.municipio_uf}` : ''}`);
      }
    });
    return Array.from(seen.entries()).map(([ibge, nome]) => ({ ibge, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  })();

  return {
    isLoading,
    contratos,
    frentes,
    clusters,
    municipiosDisponiveis,
    evolucaoTemporal,
    distribuicaoMunicipio,
    rankingFrente,
    hasData: evolucaoTemporal.length > 0 || distribuicaoMunicipio.length > 0 || rankingFrente.length > 0,
  };
}
